import { createContext, ReactNode, useContext, useMemo, useReducer } from 'react';

type FileState = {
  fileIds: string[] | null;
  scanFilesId: string | null;
  fileMutationId: number;
};

type FileAction =
  | { type: 'SET_FILE_IDS'; payload: string[] | null }
  | { type: 'SET_SCAN_FILES_ID'; payload: string | null }
  | { type: 'SET_FILE_MUTATION_ID'; payload: number }
  | { type: 'RESET' };

const initialState: FileState = {
  fileIds: null,
  scanFilesId: null,
  fileMutationId: 0,
};

function fileReducer(state: FileState, action: FileAction): FileState {
  switch (action.type) {
    case 'SET_FILE_IDS':
      return { ...state, fileIds: action.payload };
    case 'SET_SCAN_FILES_ID':
      return { ...state, scanFilesId: action.payload };
    case 'SET_FILE_MUTATION_ID':
      return { ...state, fileMutationId: action.payload };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

type FileUploaderContextValue = {
  state: FileState;
  setFileIds: (fileIds: string[] | null) => void;
  appendFileIds: (fileIds: string[]) => void;
  setScanFilesId: (scanFilesId: string | null) => void;
  updateFileMutationId: () => void;
  resetFileMutationId: () => void;
  resetFileUploader: () => void;
};

const FileUploaderContext = createContext<FileUploaderContextValue | undefined>(undefined);

export const FileProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(fileReducer, initialState);
  const { fileIds, scanFilesId, fileMutationId } = state;

  const setFileIds = (fileIds: string[] | null) => {
    dispatch({ type: 'SET_FILE_IDS', payload: fileIds });
  };

  const appendFileIds = (fileIds: string[]) => {
    const updatedFileIds = state.fileIds ? [...state.fileIds, ...fileIds] : fileIds;
    dispatch({ type: 'SET_FILE_IDS', payload: updatedFileIds });
  };

  const setScanFilesId = (scanFilesId: string | null) => {
    dispatch({ type: 'SET_SCAN_FILES_ID', payload: scanFilesId });
  };

  const updateFileMutationId = () => {
    dispatch({ type: 'SET_FILE_MUTATION_ID', payload: state.fileMutationId + 1 });
  };

  const resetFileMutationId = () => {
    dispatch({ type: 'SET_FILE_MUTATION_ID', payload: 0 });
  };

  const resetFileUploader = () => {
    dispatch({ type: 'RESET' });
  };

  const value: FileUploaderContextValue = useMemo(
    () => ({
      state: {
        fileIds,
        scanFilesId,
        fileMutationId,
      },
      setFileIds,
      appendFileIds,
      setScanFilesId,
      updateFileMutationId,
      resetFileMutationId,
      resetFileUploader,
    }),
    [fileIds, scanFilesId, fileMutationId],
  );

  return <FileUploaderContext.Provider value={value}>{children}</FileUploaderContext.Provider>;
};

export const useFileUploaderContext = (): FileUploaderContextValue => {
  const context = useContext(FileUploaderContext);
  if (context === undefined) {
    throw new Error('useFileUploaderContext must be used within a FileProvider');
  }
  return context;
};

export default FileUploaderContext;
